import React, { useEffect, useMemo, useState } from "react";
import { Sheet } from "../ui/sheet";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import CustomDataGrid from "../Shared/Table/CustomDataGrid";
import { violationsDrawerColumns } from "../Shared/Table/Columns";
import { useCreateLocalCorrectiveTemplateMutation, useExportCorrectiveTemplateMutation, useRunAuditForDeviceMutation } from "../../store/api/audit/goldenAuditViolationsApiSlice";
import { useStoreDispatch } from "../../store/hooks";
import { setOpenToast } from "../../store/helpers/helperReducers";
import { convertEpochToGMT, convertSymbols, errorMessage, exportInXML } from "../../utils/helpers";
import { DATEFORMATS } from "../../globals/globals";
import { Download, Plus, ArrowLeftRight } from "lucide-react";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useSelector } from "react-redux";
import { selectProfilePermissionsSelector } from "../../store/api/auth/selectors";
import ProfilePermissions from "../../utils/ProfilePermissions";

export interface AuditViolation {
  datamodelKey: string;
  expectedValue: string;
  actualValue: string;
  definitionId: number;
  definitionName: string;
  lastRunAt?: number;
}

interface ViolationsDrawerProps {
  open: boolean;
  onClose: () => void;
  violations: AuditViolation[];
  deviceKey: string;
  deviceLabel: string;
}

const ViolationsDrawer: React.FC<ViolationsDrawerProps> = ({ open, onClose, violations, deviceKey, deviceLabel }) => {
  const [activeTab, setActiveTab] = useState(0);
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const [exportCorrectiveTemplate] = useExportCorrectiveTemplateMutation();
  const [createLocalCorrectiveTemplate, { isLoading: isCreatingTemplate }] = useCreateLocalCorrectiveTemplateMutation();
  const [runAuditForDevice, { isLoading: isRunningAudit }] = useRunAuditForDeviceMutation();

  const violationsByDefinition = useMemo(() => {
    const grouped = new Map<string, AuditViolation[]>();
    violations.forEach((violation) => {
      const key = violation.definitionName || `Definition ${violation.definitionId}`;
      const group = grouped.get(key);
      if (group) {
        group.push(violation);
      } else {
        grouped.set(key, [violation]);
      }
    });
    return Array.from(grouped.entries());
  }, [violations]);

  useEffect(() => {
    if (activeTab >= violationsByDefinition.length) {
      setActiveTab(0);
    }
  }, [activeTab, violationsByDefinition.length]);

  const activeRows = useMemo(() => {
    if (!violationsByDefinition.length) {
      return [];
    }
    const [, groupedViolations] = violationsByDefinition[activeTab] ?? [];
    return (groupedViolations || []).map((violation, index) => ({
      id: `${violation.datamodelKey}-${index}`,
      ...violation,
    }));
  }, [activeTab, violationsByDefinition]);

  const activeDefinitionId = activeRows[0]?.definitionId;
  const activeDefinitionName = violationsByDefinition[activeTab]?.[0] ?? "corrective-template";
  const activeLastRunAt = activeRows[0]?.lastRunAt;

  const handleCompareNow = async () => {
    if (!activeDefinitionId) {
      return;
    }
    try {
      await runAuditForDevice({ definitionId: activeDefinitionId, deviceKey }).unwrap();
      dispatch(setOpenToast({ openToast: true, toastType: "success", message: "Audit run started." }));
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleExportTemplate = async () => {
    if (!activeDefinitionId) {
      return;
    }
    try {
      const response: any = await exportCorrectiveTemplate({ definitionId: activeDefinitionId, deviceKey });
      if (response?.error?.originalStatus !== 200) throw response?.error;
      exportInXML(response, `${activeDefinitionName}-${deviceKey}-corrective`);
      dispatch(setOpenToast({ openToast: true, toastType: "success", message: "Template download started." }));
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleCreateTemplate = async () => {
    if (!activeDefinitionId) {
      return;
    }
    try {
      await createLocalCorrectiveTemplate({ definitionId: activeDefinitionId, deviceKey }).unwrap();
      dispatch(setOpenToast({ openToast: true, toastType: "success", message: "Local template created." }));
    } catch (error: any) {
      const message = error?.data ? JSON.stringify(error?.data) : errorMessage(error);
      dispatch(setOpenToast({ openToast: true, toastType: "error", message }));
    }
  };

  const activeTabValue = violationsByDefinition[activeTab]?.[0] ?? "tab-0";

  const footer = (
    <div className="flex flex-col gap-3 w-full mr-0">
      <div className="flex items-center justify-end gap-2 w-full">
        <div className="flex flex-1 justify-start gap-2 text-sm">
          <span className="text-sm text-muted-foreground">
            {activeRows.length > 0 ? (
              <>
                Template: <span className="font-medium text-foreground">{convertSymbols(activeDefinitionName)}</span><span className="ml-1 text-muted-foreground">· {activeRows.length} mismatch{activeRows.length !== 1 ? "es" : ""}</span>
              </>
            ) : (
              <span className="text-muted-foreground">No config mismatches found</span>
            )}
          </span>
        </div>
        {permissions?.hasOpCodeGroup("task_management") && (
          <div className="flex items-center gap-3">
            <Button variant="outline" size="md" onClick={handleExportTemplate} disabled={!activeDefinitionId}>
              <Download className="mr-1 h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" size="md" onClick={handleCompareNow} disabled={!activeDefinitionId || isRunningAudit}>
              <CompareArrowsIcon style={{ fontSize: "20px", marginRight: "4px" }} />
              Compare Now
            </Button>
            <Button size="md" onClick={handleCreateTemplate} disabled={!activeDefinitionId || isCreatingTemplate}>
              <Plus className="mr-1 h-4 w-4" />
              Create Template
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Sheet open={open} onClose={onClose} title={`Configuration mismatch`} subtitle={`${deviceLabel} . ${violations.length} mismatch${violations.length !== 1 ? "es" : ""}`} footer={footer} useCardStyle={false} width="1030px">
      <div className="flex h-full flex-col gap-2 pt-2">
        {/* Summary */}
        {/* Definition Tabs */}
        {violationsByDefinition.length > 0 && (
          <div className="space-y-2">
            <Tabs value={activeTabValue} onValueChange={(value) => {
              const index = violationsByDefinition.findIndex(([name]) => name === value);
              if (index >= 0) setActiveTab(index);
            }} className="w-full">
              <TabsList className="bg-muted text-muted-foreground rounded-lg px-1 py-1 h-auto gap-0.5 justify-start overflow-x-auto w-fit">
                {violationsByDefinition.map(([definitionName, groupedViolations]) => (
                  <TabsTrigger
                    key={definitionName}
                    value={definitionName}
                    className="gap-2 group rounded-md px-3 py-1.5 text-medium font-extralight border-0 bg-muted text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                  >
                    {convertSymbols(definitionName)}
                    <Badge variant="secondary" className="inline-flex items-center rounded-md border py-0.5 font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 h-5 min-w-5 px-1.5 justify-center text-xs tabular-nums group-data-[state=active]:bg-primary/20 group-data-[state=active]:text-foreground">
                      {groupedViolations.length}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}

        <div className="flex gap-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Last Run :</Label>
            <p className="text-sm">{activeRows.length > 0 && activeLastRunAt ? `${convertEpochToGMT(activeLastRunAt, DATEFORMATS.TS)} (GMT)` : "—"}</p>
          </div>
        </div>

        <div className="flex-1 min-h-0" style={{ margin: 0 }}>
          {activeRows.length > 0 ? (
            <CustomDataGrid
              tableId="configurationMismatchDrawer"
              rows={activeRows}
              columns={violationsDrawerColumns}
              rowCount={activeRows.length}
              pagination={false}
              autoHeight={false}
              hideFooter={true}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              paginationMode="client"
              sortingMode="client"
              sx={{
                height: "100%",
                "& .MuiDataGrid-virtualScroller": { overflowY: "auto" },
                "& .MuiDataGrid-scrollbar--vertical": { display: "block" },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--schedule-background)",
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-md border border-border bg-card">
              <p className="text-sm text-muted-foreground">No config mismatches found</p>
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
};

export default ViolationsDrawer;
